// Fenêtres du catalogue : création et modification d'une rubrique (financier), proposition ou
// modification d'un tarif proposé (financier), simulation de tarification (sans écriture). Les
// champs sont ceux des schémas de packages/contrats (recettes-catalogue.ts, recettes-socle.ts).
import { UNITES_TEMPS_TARIF } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, DateChamp, Dialogue, GrilleChamps, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { CODES_BASE, CODES_NATURE, CODES_PERIODICITE, LIBELLES_BASE, LIBELLES_NATURE, LIBELLES_PERIODICITE, LIBELLES_UNITE_TEMPS, francs, type RubriqueReponse, type Tarif } from './donnees';
import { INVALIDER_RECETTES } from './requetes';
import styles from './recettes.module.css';

type Erreurs = { champ: string; message: string }[];
const erreurDe = (erreurs: Erreurs, champ: string) => erreurs.find((e) => e.champ === champ || e.champ.startsWith(`${champ}.`))?.message;

// --- Rubrique -----------------------------------------------------------------------------

const RUBRIQUE_VIDE = { code: '', libelle: '', nature: 'redevance', fondement: 'à confirmer', periodicite: 'unique', base_calcul: 'forfait', paiement_partiel_autorise: true, remboursable: false, categorie: 'principale', rubrique_parent_id: '', active: true };

export function FormulaireRubrique({ ouvert, onFermer, rubrique, principales }: { ouvert: boolean; onFermer: () => void; rubrique?: RubriqueReponse | null; principales: RubriqueReponse[] }) {
  const [s, setS] = useState(() => (rubrique ? { ...RUBRIQUE_VIDE, ...rubrique, rubrique_parent_id: rubrique.rubrique_parent_id ?? '' } : RUBRIQUE_VIDE));
  const modification = Boolean(rubrique);
  const action = useAction({
    executer: () =>
      modification
        ? requete<RubriqueReponse>(`/recettes/rubriques/${rubrique?.id}`, { methode: 'PATCH', corps: { libelle: s.libelle, fondement: s.fondement, paiement_partiel_autorise: s.paiement_partiel_autorise, remboursable: s.remboursable, active: s.active } })
        : requete<RubriqueReponse>('/recettes/rubriques', {
            methode: 'POST',
            corps: {
              code: s.code,
              libelle: s.libelle,
              nature: s.nature,
              fondement: s.fondement || 'à confirmer',
              periodicite: s.periodicite,
              base_calcul: s.base_calcul,
              paiement_partiel_autorise: s.paiement_partiel_autorise,
              remboursable: s.remboursable,
              categorie: s.categorie,
              ...(s.categorie !== 'principale' && s.rubrique_parent_id ? { rubrique_parent_id: s.rubrique_parent_id } : {}),
            },
          }),
    succes: (r) => (modification ? `Rubrique « ${r.libelle} » modifiée.` : `Rubrique « ${r.libelle} » créée ; proposez-lui un tarif.`),
    invalider: INVALIDER_RECETTES,
    apres: () => {
      onFermer();
      if (!modification) setS(RUBRIQUE_VIDE);
    },
  });
  const e = (champ: string) => erreurDe(action.erreursChamps, champ);
  const champ = (cle: keyof typeof RUBRIQUE_VIDE) => (ev: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: ev.target.value }));
  const coche = (cle: 'paiement_partiel_autorise' | 'remboursable' | 'active') => (ev: { target: { checked: boolean } }) => setS((x) => ({ ...x, [cle]: ev.target.checked }));
  const complet = s.libelle.trim().length >= 2 && (modification || /^[a-z][a-z0-9_]{1,62}$/.test(s.code)) && (s.categorie === 'principale' || s.rubrique_parent_id);
  return (
    <Dialogue
      ouvert={ouvert}
      titre={modification ? `Modifier la rubrique ${rubrique?.code ?? ''}` : 'Nouvelle rubrique'}
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton chargement={action.enCours} disabled={!complet} onClick={() => action.lancer(undefined)}>
            {modification ? 'Enregistrer' : 'Créer la rubrique'}
          </Bouton>
        </>
      }
    >
      <p>Nature, périodicité et base de calcul ne changent plus après la création : un changement se fait par une nouvelle rubrique.</p>
      <GrilleChamps>
        {!modification ? <Champ id="rubrique-code" libelle="Code" aide="Minuscules, chiffres et soulignés (par exemple droit_place_marche)" value={s.code} required erreur={e('code')} onChange={champ('code')} /> : null}
        <Champ id="rubrique-libelle" libelle="Libellé" value={s.libelle} required erreur={e('libelle')} onChange={champ('libelle')} />
        {!modification ? <Selection id="rubrique-nature" libelle="Nature" value={s.nature} options={CODES_NATURE.map((c) => ({ valeur: c, libelle: LIBELLES_NATURE[c] ?? c }))} erreur={e('nature')} onChange={champ('nature')} /> : null}
        {!modification ? <Selection id="rubrique-periodicite" libelle="Périodicité de facturation" value={s.periodicite} options={CODES_PERIODICITE.map((c) => ({ valeur: c, libelle: LIBELLES_PERIODICITE[c] ?? c }))} erreur={e('periodicite')} onChange={champ('periodicite')} /> : null}
        {!modification ? <Selection id="rubrique-base" libelle="Base de calcul" value={s.base_calcul} options={CODES_BASE.map((c) => ({ valeur: c, libelle: LIBELLES_BASE[c] ?? c }))} erreur={e('base_calcul')} onChange={champ('base_calcul')} /> : null}
        {!modification ? (
          <Selection id="rubrique-categorie" libelle="Catégorie" value={s.categorie} options={[{ valeur: 'principale', libelle: 'Rubrique principale' }, { valeur: 'exoneration', libelle: 'Exonération liée à une rubrique' }, { valeur: 'penalite', libelle: 'Pénalité liée à une rubrique' }]} onChange={champ('categorie')} />
        ) : null}
        {!modification && s.categorie !== 'principale' ? (
          <Selection id="rubrique-parent" libelle="Rubrique principale liée" value={s.rubrique_parent_id} required optionVide="Choisir" options={principales.map((r) => ({ valeur: r.id, libelle: r.libelle }))} erreur={e('rubrique_parent_id')} onChange={champ('rubrique_parent_id')} />
        ) : null}
      </GrilleChamps>
      <ZoneTexte id="rubrique-fondement" libelle="Fondement juridique" aide="« à confirmer » est admis tant que la référence n'est pas validée" value={s.fondement} erreur={e('fondement')} onChange={champ('fondement')} />
      <CaseACocher id="rubrique-partiel" libelle="Paiement partiel autorisé" checked={s.paiement_partiel_autorise} onChange={coche('paiement_partiel_autorise')} />
      <CaseACocher id="rubrique-remboursable" libelle="Remboursable" checked={s.remboursable} onChange={coche('remboursable')} />
      {modification ? <CaseACocher id="rubrique-active" libelle="Rubrique active" checked={s.active} onChange={coche('active')} /> : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

// --- Tarif --------------------------------------------------------------------------------

const TARIF_VIDE = { mode: 'montant', montant: '', pourcentage: '', unite: '', unite_temps: 'aucune', debut: '', fin: '', acte_reference: '', fictif: false };

export function FormulaireTarif({ ouvert, onFermer, rubrique, tarif }: { ouvert: boolean; onFermer: () => void; rubrique: RubriqueReponse; tarif?: Tarif | null }) {
  const [s, setS] = useState(() =>
    tarif
      ? { mode: tarif.montant !== null ? 'montant' : 'pourcentage', montant: tarif.montant === null ? '' : String(tarif.montant), pourcentage: tarif.pourcentage === null ? '' : String(tarif.pourcentage), unite: tarif.unite, unite_temps: tarif.unite_temps, debut: tarif.debut, fin: tarif.fin ?? '', acte_reference: tarif.acte_reference ?? '', fictif: tarif.fictif }
      : TARIF_VIDE,
  );
  const modification = Boolean(tarif);
  const corps = () => ({
    ...(s.mode === 'montant' ? { montant: Number(s.montant) } : { pourcentage: Number(s.pourcentage.replace(',', '.')) }),
    unite: s.unite,
    unite_temps: s.unite_temps,
    debut: s.debut,
    ...(s.fin ? { fin: s.fin } : {}),
    ...(s.acte_reference.trim() ? { acte_reference: s.acte_reference.trim() } : {}),
    ...(modification ? {} : { fictif: s.fictif }),
  });
  const action = useAction({
    executer: () => (modification ? requete<RubriqueReponse>(`/recettes/tarifs/${tarif?.id}`, { methode: 'PATCH', corps: corps() }) : requete<RubriqueReponse>(`/recettes/rubriques/${rubrique.id}/tarifs`, { methode: 'POST', corps: corps() })),
    succes: modification ? 'Tarif proposé modifié.' : 'Tarif proposé ; son approbation revient au maire ou président (quatre yeux).',
    invalider: INVALIDER_RECETTES,
    apres: () => {
      onFermer();
      if (!modification) setS(TARIF_VIDE);
    },
  });
  const e = (champ: string) => erreurDe(action.erreursChamps, champ);
  const champ = (cle: keyof typeof TARIF_VIDE) => (ev: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: ev.target.value }));
  const valeurOk = s.mode === 'montant' ? /^\d+$/.test(s.montant) : Number(s.pourcentage.replace(',', '.')) > 0;
  const complet = valeurOk && s.unite.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(s.debut);
  return (
    <Dialogue
      ouvert={ouvert}
      titre={modification ? `Modifier le tarif proposé de ${rubrique.libelle}` : `Proposer un tarif pour ${rubrique.libelle}`}
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton chargement={action.enCours} disabled={!complet} onClick={() => action.lancer(undefined)}>
            {modification ? 'Enregistrer' : 'Proposer le tarif'}
          </Bouton>
        </>
      }
    >
      <p>
        Base de calcul de la rubrique : {LIBELLES_BASE[rubrique.base_calcul] ?? rubrique.base_calcul}. Le tarif proposé n&apos;entre en vigueur qu&apos;après son approbation par une autre personne ; un tarif approuvé ne se modifie plus.
      </p>
      <GrilleChamps>
        <Selection id="tarif-mode" libelle="Forme du tarif" value={s.mode} options={[{ valeur: 'montant', libelle: 'Montant en francs CFA' }, { valeur: 'pourcentage', libelle: 'Pourcentage' }]} onChange={champ('mode')} />
        {s.mode === 'montant' ? (
          <Champ id="tarif-montant" libelle="Montant (F)" inputMode="numeric" value={s.montant} required erreur={e('montant')} onChange={champ('montant')} />
        ) : (
          <Champ id="tarif-pourcentage" libelle="Pourcentage" inputMode="decimal" value={s.pourcentage} required erreur={e('pourcentage')} onChange={champ('pourcentage')} />
        )}
        <Champ id="tarif-unite" libelle="Unité" aide="Par exemple « par m² et par mois », « par dossier », « du montant déclaré »" value={s.unite} required erreur={e('unite')} onChange={champ('unite')} />
        <Selection id="tarif-unite-temps" libelle="Unité de temps" value={s.unite_temps} options={UNITES_TEMPS_TARIF.map((c) => ({ valeur: c, libelle: LIBELLES_UNITE_TEMPS[c] ?? c }))} erreur={e('unite_temps')} onChange={champ('unite_temps')} />
        <DateChamp id="tarif-debut" libelle="Début de validité" value={s.debut} required erreur={e('debut')} onChange={champ('debut')} />
        <DateChamp id="tarif-fin" libelle="Fin de validité" facultatif value={s.fin} erreur={e('fin')} onChange={champ('fin')} />
      </GrilleChamps>
      <Champ id="tarif-acte" libelle="Référence de l'acte" aide="Délibération ou arrêté ; sans acte, un tarif approuvé ouvre une anomalie" facultatif value={s.acte_reference} erreur={e('acte_reference')} onChange={champ('acte_reference')} />
      {!modification ? <CaseACocher id="tarif-fictif" libelle="Tarif fictif de présentation (mention « (fictif) » partout)" checked={s.fictif} onChange={(ev) => setS((x) => ({ ...x, fictif: ev.target.checked }))} /> : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

// --- Simulation ---------------------------------------------------------------------------

interface ResultatTarification {
  rubrique_id: string;
  tarif_id: string;
  montant: number;
  devise: string;
  regle: string;
  fictif: boolean;
}

export function SimulationTarification({ ouvert, onFermer, rubriques }: { ouvert: boolean; onFermer: () => void; rubriques: RubriqueReponse[] }) {
  const [s, setS] = useState({ rubrique: '', surface: '', quantite: '', jours: '', reference: '', debut: '', fin: '' });
  const [resultat, setResultat] = useState<ResultatTarification | null>(null);
  const rubrique = rubriques.find((r) => r.code === s.rubrique);
  const action = useAction({
    executer: () =>
      requete<ResultatTarification>('/recettes/tarification', {
        methode: 'POST',
        corps: {
          rubrique_code: s.rubrique,
          base: {
            ...(s.surface ? { surface_m2: Number(s.surface.replace(',', '.')) } : {}),
            ...(s.quantite ? { quantite: Number(s.quantite) } : {}),
            ...(s.jours ? { jours: Number(s.jours) } : {}),
            ...(s.reference ? { montant_reference: Number(s.reference) } : {}),
          },
          periode: { debut: s.debut, fin: s.fin || s.debut },
        },
      }),
    apres: (r) => setResultat(r),
  });
  const champ = (cle: keyof typeof s) => (ev: { target: { value: string } }) => {
    setResultat(null);
    setS((x) => ({ ...x, [cle]: ev.target.value }));
  };
  const base = rubrique?.base_calcul;
  return (
    <Dialogue ouvert={ouvert} titre="Simuler une tarification" onFermer={onFermer} actions={<Bouton variante="secondaire" onClick={onFermer}>Fermer</Bouton>}>
      <p>Montant et règle de calcul du tarif approuvé en vigueur au début de la période ; aucune créance n&apos;est émise.</p>
      <GrilleChamps>
        <Selection id="simulation-rubrique" libelle="Rubrique" value={s.rubrique} required optionVide="Choisir" options={rubriques.filter((r) => r.categorie === 'principale' && r.active).map((r) => ({ valeur: r.code, libelle: r.libelle }))} onChange={champ('rubrique')} />
        {base === 'surface' ? <Champ id="simulation-surface" libelle="Surface (m²)" inputMode="decimal" value={s.surface} onChange={champ('surface')} /> : null}
        {base === 'unite' ? <Champ id="simulation-quantite" libelle="Quantité" inputMode="numeric" value={s.quantite} onChange={champ('quantite')} /> : null}
        {base === 'pourcentage' ? <Champ id="simulation-reference" libelle="Montant de référence (F)" inputMode="numeric" value={s.reference} onChange={champ('reference')} /> : null}
        <DateChamp id="simulation-debut" libelle="Début de la période" value={s.debut} required onChange={champ('debut')} />
        <DateChamp id="simulation-fin" libelle="Fin de la période" value={s.fin} facultatif onChange={champ('fin')} />
      </GrilleChamps>
      <div className={styles.actions}>
        <Bouton variante="secondaire" chargement={action.enCours} disabled={!s.rubrique || !/^\d{4}-\d{2}-\d{2}$/.test(s.debut)} onClick={() => action.lancer(undefined)}>
          Calculer
        </Bouton>
      </div>
      {resultat ? (
        <div role="status">
          <p>
            Montant : <strong>{francs(resultat.montant)}</strong>
            {resultat.fictif ? ' (tarif fictif de présentation)' : ''}
          </p>
          <p className={styles.regle}>{resultat.regle}</p>
        </div>
      ) : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}
